// Packages
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Typography, InputAdornment, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useZustandStore from 'zustandStore';

// Hooks
import useAuthScope from 'hooks/useAuthScope';

// Components
import LinkModal from 'components/LinkModal';
import ModalContainer from 'components/ModalContainer';
import { SolidButton } from 'components/buttons/SolidButton';
import { OutlineButton } from 'components/buttons/OutlineButton';

import mainStyle from '../../LdapServer/mainStyle.module.scss';
import { StyledBox } from '../../LdapServer/components/StyledBox';
import { StyledInput } from '../../LdapServer/components/StyledInput';
import { MenuSelect } from 'components/Select/menuSelect';

// Icons
import { ReactComponent as CloseIcon } from 'assets/images/icons/icn_close.svg';
import { ReactComponent as ShowIcon } from 'assets/images/icons/icn_input_show_hover.svg';
import { ReactComponent as HideIcon } from 'assets/images/icons/icn_input_hide.svg';

// Helper
import { getInitSelectedLdap, getIsServerInvalid, getIsPortInvalid, getIsFormInvalid, getIsBaseDNInvalid } from '../helper/createAndUpdate';

const CreateAndEditModal = ({ selectedLdap, setSelectedLdap, actions, setActions, showEditGeneral }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { auth } = useAuthScope();

  const isCreateMode = useMemo(() => Boolean(actions?.isCreate) || !selectedLdap?.id, [actions, selectedLdap]);
  const [selectedModalLdap, setSelectedModalLdap] = useState(getInitSelectedLdap({ selectedLdap }));
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [dirty, setDirty] = useState({});
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const serviceStatus = useZustandStore(state => state.serviceStatus);
  const isUnityPro = serviceStatus?.services.unityPro;

  const ldapAuthOptionsConst = useMemo(
    () => [
      { label: 'Simple', value: 'Simple' },
      { label: 'TLS', value: 'TLS' },
    ],
    [],
  );

  useEffect(() => {
    // If opening Create modal, initialize empty fields and default auth to first option
    if (actions?.isCreate) {
      setSelectedModalLdap({
        name: '',
        server: '',
        port: '',
        authMode: ldapAuthOptionsConst[0]?.value || '',
        username: '',
        password: '',
        baseDN: '',
        accountAttr: '',
        identity: '',
        autoCopy: false,
      });
      setDirty({});
      return;
    }

    // Edit or external selectedLdap change: normalize incoming keys then load
    if (selectedLdap) {
      const normalized = {
        id: selectedLdap.id,
        name: selectedLdap.name ?? selectedLdap?.profileName ?? '',
        server: selectedLdap.server ?? selectedLdap?.ldapServer ?? '',
        port: selectedLdap.port ?? '',
        authMode: selectedLdap.authMode ?? selectedLdap.authenticationMode ?? '',
        username: selectedLdap.username ?? '',
        password: selectedLdap.password ?? '',
        baseDN: selectedLdap.baseDN ?? selectedLdap.baseDn ?? '',
        accountAttr: selectedLdap.accountAttr ?? selectedLdap.accountAttribute ?? '',
        identity: selectedLdap.identity ?? '',
        autoCopy: typeof selectedLdap.autoCopy === 'boolean' ? selectedLdap.autoCopy : !!selectedLdap.auto_copy,
      };

      // If autoCopy is enabled in the incoming data, compute identity from accountAttr, username, baseDN
      if (normalized.autoCopy) {
        normalized.identity = `${normalized.accountAttr || ''}=${normalized.username || ''},${normalized.baseDN || ''}`;
      }

      setSelectedModalLdap({ ...getInitSelectedLdap({}), ...normalized });
      setDirty({});
      return;
    }

    // Fallback: no selectedLdap
    setSelectedModalLdap(getInitSelectedLdap({ selectedLdap }));
    setDirty({});
  }, [selectedLdap, actions?.isCreate, ldapAuthOptionsConst]);

  const onCreateOrApply = () => {
    if (!isUnityPro) {
      setIsLinkModalOpen(true);
      return; // Open the link modal first, then proceed with saving after user confirmation
    }

    // Normalize baseDN: remove spaces around commas so it's like "ou=example,dc=com"
    const normalizedBaseDN = selectedModalLdap?.baseDN
      ? String(selectedModalLdap.baseDN)
          .trim()
          .replace(/\s*,\s*/g, ',')
      : selectedModalLdap?.baseDN;

    // If autoCopy enabled, rebuild identity using normalized baseDN
    const normalizedIdentity = selectedModalLdap?.autoCopy
      ? `${selectedModalLdap.accountAttr || ''}=${selectedModalLdap.username || ''},${normalizedBaseDN || ''}`
      : selectedModalLdap?.identity;

    const payload = structuredClone({ ...selectedModalLdap, baseDN: normalizedBaseDN, identity: normalizedIdentity });

    setSelectedLdap(payload);
    if (isCreateMode) setActions({ ...{ isCreate: true, isCreateAndSave: true } });
    else setActions({ ...{ isEdit: true, isEditAndSave: true } });
  };

  const onCancel = () => setActions({ ...{ isCreate: false, isEdit: false } });

  const onUpdateValue = ({ key, value }) => {
    setSelectedModalLdap(prev => {
      const next = { ...prev, [key]: value };

      // If autoCopy enabled, identity should be auto-generated from accountAttr, username, baseDN
      const autoCopyEnabled = key === 'autoCopy' ? value === true : next.autoCopy === true;

      if (autoCopyEnabled) {
        const accountAttr = key === 'accountAttr' ? value : next.accountAttr;
        const username = key === 'username' ? value : next.username;
        const baseDN = key === 'baseDN' ? value : next.baseDN;

        next.identity = `${accountAttr || ''}=${username || ''},${baseDN || ''}`;

        if (next.identity === '=,') {
          next.identity = '';
        }
      }

      return next;
    });

    setDirty(prev => {
      // when autoCopy is turned on, mark identity as not dirty (it's auto-filled)
      if (key === 'autoCopy' && value === true) {
        return { ...prev, [key]: true, identity: false };
      }

      return { ...prev, [key]: true };
    });
  };

  const ldapAuthOptions = useMemo(
    () => ldapAuthOptionsConst.map(o => ({ ...o, isSelected: selectedModalLdap?.authMode === o.value })),
    [ldapAuthOptionsConst, selectedModalLdap?.authMode],
  );

  const isSubmitDisabled = useMemo(() => {
    if (!showEditGeneral || getIsFormInvalid({ selectedLdap: selectedModalLdap })) return true;
    return false;
  }, [selectedModalLdap]);

  return (
    <ModalContainer display={actions?.isCreate || actions?.isEdit} modalWidth="850px">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
          sx={{ mb: '30px', ml: '20px', color: theme.palette.primary.main, fontSize: theme.typography.customFontSize[5], fontWeight: theme.typography.customFontWeight[3] }}
        >
          {isCreateMode ? 'Create' : 'Edit'} LDAP Profile
        </Typography>
        <Button onClick={onCancel} sx={{ marginRight: '-10px', marginTop: '-28px', backgroundColor: theme.palette.primary.transparent, boxShadow: 'none' }}>
          <CloseIcon />
        </Button>
      </Box>

      <Box sx={{ ml: '20px' }}>
        <Box sx={{ marginBottom: 3 }}>
          <StyledBox sx={{ ml: '32px', mb: '0px', gap: 5 }}>
            <Typography className={mainStyle['collapse-typography']}>Profile Name</Typography>
            <StyledInput
              value={selectedModalLdap?.name || ''}
              disabled={!showEditGeneral}
              isInvalid={dirty.name && !selectedModalLdap?.name}
              onChange={e => onUpdateValue({ key: 'name', value: e.target.value })}
            />
          </StyledBox>
        </Box>

        <StyledBox sx={{ ml: '32px', mb: 3, gap: 5 }}>
          <Typography className={mainStyle['collapse-typography']}>LDAP Server</Typography>
          <StyledInput
            placeholder="e.g. 10.10.10.10"
            value={selectedModalLdap?.server || ''}
            disabled={!showEditGeneral}
            isInvalid={dirty.server && getIsServerInvalid({ server: selectedModalLdap?.server })}
            helperText={dirty.server && getIsServerInvalid({ server: selectedModalLdap?.server }) ? 'Please specify a valid address for the LDAP server.' : ''}
            onChange={e => onUpdateValue({ key: 'server', value: e.target.value })}
          />
        </StyledBox>

        <StyledBox sx={{ ml: '32px', mb: 3, gap: 5 }}>
          <Typography className={mainStyle['collapse-typography']}>Port</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StyledInput
              type="number"
              placeholder="1-65535"
              value={selectedModalLdap?.port || ''}
              disabled={!showEditGeneral}
              isInvalid={dirty.port && getIsPortInvalid({ port: selectedModalLdap?.port })}
              helperText={dirty.port && getIsPortInvalid({ port: selectedModalLdap?.port }) ? 'Please enter a valid port number between 1 and 65535.' : ''}
              onChange={e => onUpdateValue({ key: 'port', value: e.target.value })}
            />
            <Typography sx={{ color: theme.palette.primary.greyPrimary }}>(1-65535)</Typography>
          </Box>
        </StyledBox>

        <StyledBox sx={{ ml: '32px', mb: 3, gap: 5 }}>
          <Typography className={mainStyle['collapse-typography']}>Authentication Mode</Typography>
          <MenuSelect
            width="350px"
            disabled={!showEditGeneral}
            value={selectedModalLdap?.authMode ?? ''}
            onChange={opt => onUpdateValue({ key: 'authMode', value: opt.value })}
            options={ldapAuthOptions}
          />
        </StyledBox>

        <StyledBox sx={{ ml: '32px', mb: 3, gap: 5 }}>
          <Typography className={mainStyle['collapse-typography']}>Username</Typography>
          <StyledInput
            value={selectedModalLdap?.username || ''}
            disabled={!showEditGeneral}
            isInvalid={dirty.username && !selectedModalLdap?.username}
            onChange={e => onUpdateValue({ key: 'username', value: e.target.value })}
          />
        </StyledBox>

        <StyledBox sx={{ ml: '32px', mb: 3, gap: 5 }}>
          <Typography className={mainStyle['collapse-typography']}>Password</Typography>
          <StyledInput
            type={isShowPassword ? 'text' : 'password'}
            value={selectedModalLdap?.password || ''}
            disabled={!showEditGeneral}
            isInvalid={dirty.password && !selectedModalLdap?.password}
            onChange={e => onUpdateValue({ key: 'password', value: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setIsShowPassword(prev => !prev)} edge="end" sx={{ padding: 1 }}>
                    {isShowPassword ? <ShowIcon /> : <HideIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </StyledBox>

        <StyledBox sx={{ ml: '32px', mb: 3, gap: 5 }}>
          <Typography className={mainStyle['collapse-typography']}>Base DN</Typography>
          <StyledInput
            placeholder="e.g. ou=dlink, dc=nuclias, dc=com"
            value={selectedModalLdap?.baseDN || ''}
            disabled={!showEditGeneral}
            isInvalid={(dirty.baseDN && !selectedModalLdap?.baseDN) || (selectedModalLdap?.baseDN && getIsBaseDNInvalid({ baseDN: selectedModalLdap?.baseDN }))}
            onChange={e => onUpdateValue({ key: 'baseDN', value: e.target.value })}
          />
        </StyledBox>

        <StyledBox sx={{ ml: '32px', mb: 3, gap: 5 }}>
          <Typography className={mainStyle['collapse-typography']}>Account Attribute</Typography>
          <StyledInput
            placeholder="e.g. cn"
            value={selectedModalLdap?.accountAttr || ''}
            disabled={!showEditGeneral}
            isInvalid={dirty.accountAttr && !selectedModalLdap?.accountAttr}
            onChange={e => onUpdateValue({ key: 'accountAttr', value: e.target.value })}
          />
        </StyledBox>

        <StyledBox sx={{ ml: '32px', mb: 3, gap: 5 }}>
          <Typography className={mainStyle['collapse-typography']}>Identity</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StyledInput
              value={selectedModalLdap?.identity || ''}
              disabled={!showEditGeneral || selectedModalLdap?.autoCopy}
              isInvalid={dirty.identity && !selectedModalLdap?.identity}
              onChange={e => onUpdateValue({ key: 'identity', value: e.target.value || '' })}
            />
            <FormControlLabel
              control={<Checkbox checked={selectedModalLdap?.autoCopy || false} onChange={e => onUpdateValue({ key: 'autoCopy', value: e.target.checked })} />}
              label="Auto Copy"
              sx={{ ml: 0 }}
              disabled={!showEditGeneral}
            />
          </Box>
        </StyledBox>
      </Box>

      <Box sx={{ position: 'sticky', bottom: 0, display: 'flex', pt: '30px', pb: '20px', justifyContent: 'center', gap: '20px', width: '100%', backgroundColor: 'primary.white' }}>
        <OutlineButton width="100px" height="32px" onClick={onCancel}>
          Cancel
        </OutlineButton>
        <SolidButton width="100px" height="32px" disabled={isSubmitDisabled} onClick={onCreateOrApply}>
          {isCreateMode ? 'Create' : 'Apply'}
        </SolidButton>
      </Box>

      <LinkModal
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? '/msp/license' : '/license');
        }}
        content="LDAP Profile is a Nuclias Unity Pro feature. Please subscribe to Nuclias Unity Pro to save these changes."
        transparentBackdrop
      />
    </ModalContainer>
  );
};

export default CreateAndEditModal;
